import { useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { formatDateOnlyLocal } from '@erp/shared-utils';
import type {
  GetSupplyDemandLinesResponse,
  LineReceipt,
  LineSalesDemand,
  LineTransferOut,
} from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSupplyDemandLines } from 'src/api/rpc/hooks/procurement/useReplenishmentDetail';
import { NO_FACT, formatQuantity } from '../replenishment/utils/lineFacts';
import { isPastDue } from '../replenishment/utils/lineSupply';

export type SupplyDemandLinesData = Pick<
  GetSupplyDemandLinesResponse,
  'receipts' | 'salesOrders' | 'stagedTransfers' | 'transfersOut'
>;

/**
 * Where these lines are, so an open transfer out can open the open orders at
 * the warehouse it goes to (GEN-3284): Robert's tree from a DC to a hub to the
 * branches and customers behind it.
 */
export interface OpenOrdersTree {
  /** The item's ProductV3 key. */
  sku: string;
  /** The location the lines are for: the root of the tree. */
  warehouseId: string;
}

/** The warehouses opened so far, root first, under one item. */
interface TreePath {
  sku: string;
  path: readonly string[];
}

/** DC, hub, branch and one more: deeper than any Rexel supply chain. */
const MAX_TREE_DEPTH = 4;

/**
 * A transfer opens when its receiver has open orders and is not already on
 * the path, so a transfer back up the chain never loops.
 */
function canOpen(transfer: LineTransferOut, tree: TreePath) {
  return (
    transfer.receivingWarehouseId !== null &&
    transfer.receivingOpenOrders > 0 &&
    tree.path.length < MAX_TREE_DEPTH &&
    !tree.path.includes(transfer.receivingWarehouseId)
  );
}

function Section({
  title,
  total,
  children,
  empty,
}: {
  title: ReactNode;
  total: number;
  children: ReactNode;
  empty: boolean;
}) {
  return (
    <section className="min-w-0">
      <div className="mb-1 flex items-baseline justify-between gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <span>{title}</span>
        <span className="tabular-nums">{formatQuantity(total)}</span>
      </div>
      {empty ? (
        <p className="text-muted-foreground">{NO_FACT}</p>
      ) : (
        <table className="w-full">
          <tbody>{children}</tbody>
        </table>
      )}
    </section>
  );
}

function ReceiptReference({ receipt }: { receipt: LineReceipt }) {
  const gt = useGT();
  // A PO opens its page; a transfer order has none, so it is text.
  if (receipt.kind === 'transfer_order') {
    return (
      <span className="font-mono">
        {receipt.number ??
          gt('Transfer order', { context: 'line supply tab: unnumbered TO' })}
      </span>
    );
  }
  return (
    <Link
      to="/procurement/purchase-orders/$purchaseOrderId"
      params={{ purchaseOrderId: receipt.orderId }}
      className="font-mono underline-offset-4 hover:underline focus-visible:underline"
    >
      {receipt.number ??
        gt('Purchase order', { context: 'line supply tab: unnumbered PO' })}
    </Link>
  );
}

/** The order number, and its line on the order when the source keeps one. */
function OrderReference({
  number,
  lineNumber,
  toggle,
}: {
  number: string;
  lineNumber: number | null;
  toggle?: ReactNode;
}) {
  const gt = useGT();
  return (
    <td className="whitespace-nowrap py-0.5 pr-2">
      {toggle}
      <span className="font-mono">{number}</span>
      {lineNumber !== null && (
        <span className="ml-1 text-muted-foreground">
          {gt('ln {line}', {
            context: 'line supply tab: the line number on an AS400 order',
            line: lineNumber,
          })}
        </span>
      )}
    </td>
  );
}

/**
 * The room a tree caret takes, for a row without one: under a transfer, the
 * receiver's sales orders and transfers then start their order numbers at the
 * same column.
 */
function CaretRoom() {
  return <span aria-hidden className="mr-1 inline-block size-3 align-middle" />;
}

/**
 * Who a sales order is for, and whether it is bought for them only. In a
 * tree, `caretRoom` lines its number up with the transfers' around it.
 */
function SalesOrderRow({
  order,
  caretRoom = false,
}: {
  order: LineSalesDemand;
  caretRoom?: boolean;
}) {
  const gt = useGT();
  const customer = [order.customerName, order.customerCode]
    .filter((part) => part !== null)
    .join(' · ');
  return (
    <tr>
      <OrderReference
        number={order.number}
        lineNumber={order.lineNumber}
        toggle={caretRoom ? <CaretRoom /> : undefined}
      />
      <td className="w-full max-w-0 truncate py-0.5 pr-2" title={customer}>
        {order.customerName ?? order.customerCode ?? NO_FACT}
      </td>
      <td className="whitespace-nowrap py-0.5 pr-2">
        {order.productOnTheFly && (
          <span
            className="rounded bg-muted px-1 text-[10px] font-medium"
            title={gt(
              'Product on the fly: bought for this customer, not stocked',
              {
                context: 'line supply tab: tooltip of the PTF order badge',
              },
            )}
          >
            <T context="line supply tab: badge on a product-on-the-fly order">
              PTF
            </T>
          </span>
        )}
      </td>
      <td className="whitespace-nowrap py-0.5 pr-2 text-muted-foreground">
        {order.date ?? NO_FACT}
      </td>
      <td className="py-0.5 text-right tabular-nums">
        {formatQuantity(order.quantity)}
      </td>
    </tr>
  );
}

function ReceivingOrdersError({ onRetry }: { onRetry: () => void }) {
  return (
    <p role="alert" className="flex items-center gap-2 text-muted-foreground">
      <T context="line supply tab: the receiving warehouse's open orders failed to load">
        Couldn't load the open orders.
      </T>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        onClick={onRetry}
      >
        <T context="line supply tab: load the receiving warehouse's open orders again">
          Retry
        </T>
      </Button>
    </p>
  );
}

/**
 * The open orders for the item at the warehouse a transfer goes to: the
 * demand that may sit behind the transfer. They are that warehouse's own open
 * orders, not orders linked to this transfer: AS400 keeps that link only in
 * the transfer's notes, which are not ingested. Read when opened.
 */
function ReceivingOpenOrders({
  tree,
  warehouseId,
  label,
}: {
  tree: TreePath;
  warehouseId: string;
  label: string;
}) {
  const gt = useGT();
  const { data, isError, refetch } = useSupplyDemandLines(
    { sku: tree.sku, warehouseId },
    true,
  );
  if (data === undefined) {
    return isError ? (
      <ReceivingOrdersError onRetry={() => void refetch()} />
    ) : (
      <p className="text-muted-foreground">
        <T context="line supply tab: the receiving warehouse's open orders are loading">
          Loading…
        </T>
      </p>
    );
  }
  const empty = data.salesOrders.length === 0 && data.transfersOut.length === 0;
  return (
    <div className="border-l border-border pl-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {gt('Open orders for this item at {warehouse}', {
          context:
            'line supply tab: heading of the open orders at the warehouse a transfer goes to',
          warehouse: label,
        })}
      </div>
      {empty ? (
        <p className="text-muted-foreground">{NO_FACT}</p>
      ) : (
        <table className="w-full">
          <tbody>
            {data.salesOrders.map((order) => (
              <SalesOrderRow key={order.lineId} order={order} caretRoom />
            ))}
            {data.transfersOut.map((transfer) => (
              <TransferOutRow
                key={transfer.lineId}
                transfer={transfer}
                tree={{ sku: tree.sku, path: [...tree.path, warehouseId] }}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TreeToggle({
  open,
  onToggle,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
}) {
  const Caret = open ? CaretDownIcon : CaretRightIcon;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={label}
      title={label}
      className="mr-1 inline-flex align-middle text-muted-foreground hover:text-foreground"
    >
      <Caret aria-hidden className="size-3" />
    </button>
  );
}

/** The receiving warehouse as its code, else its name; null when none. */
function receivingCode(transfer: LineTransferOut): string | null {
  return transfer.receivingWarehouseCode ?? transfer.receivingWarehouseName;
}

/** The receiving warehouse as its code and name, for a heading. */
function receivingLabel(transfer: LineTransferOut): string {
  return [transfer.receivingWarehouseCode, transfer.receivingWarehouseName]
    .filter((part) => part !== null)
    .join(' ');
}

/**
 * Where an open transfer goes: the receiving warehouse's code, then its name.
 * A transfer that names no destination still lists, with no receiver.
 */
function ReceivingWarehouseCell({ transfer }: { transfer: LineTransferOut }) {
  const gt = useGT();
  const code = receivingCode(transfer);
  return (
    <td
      className="w-full max-w-0 truncate py-0.5 pr-2"
      title={transfer.receivingWarehouseName ?? undefined}
    >
      {code === null
        ? NO_FACT
        : gt('to {warehouse}', {
            context: 'line supply tab: the warehouse an open transfer goes to',
            warehouse: code,
          })}
      {transfer.receivingWarehouseCode !== null &&
        transfer.receivingWarehouseName !== null && (
          <span className="ml-1 text-muted-foreground">
            {transfer.receivingWarehouseName}
          </span>
        )}
    </td>
  );
}

/**
 * The caret opening a transfer's receiver. In a tree, a transfer with nothing
 * to open keeps the caret's room, so the transfer numbers line up.
 */
function TransferCaret({
  transfer,
  tree,
  open,
  onToggle,
}: {
  transfer: LineTransferOut;
  tree: TreePath | undefined;
  open: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  if (tree === undefined) return null;
  if (!canOpen(transfer, tree)) return <CaretRoom />;
  return (
    <TreeToggle
      open={open}
      onToggle={onToggle}
      label={gt('Open orders for this item at {warehouse} ({count})', {
        context:
          'line supply tab: button opening the open orders at the warehouse a transfer goes to, with how many',
        warehouse: receivingCode(transfer) ?? NO_FACT,
        count: transfer.receivingOpenOrders,
      })}
    />
  );
}

/**
 * An open transfer shipping out, and the warehouse receiving it. With a tree,
 * a transfer whose receiver has open orders for the item opens them below.
 */
function TransferOutRow({
  transfer,
  tree,
}: {
  transfer: LineTransferOut;
  tree?: TreePath;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const toggle = (
    <TransferCaret
      transfer={transfer}
      tree={tree}
      open={open}
      onToggle={() => setOpen((value) => !value)}
    />
  );
  return (
    <>
      <tr>
        <OrderReference
          number={transfer.number}
          lineNumber={transfer.lineNumber}
          toggle={toggle}
        />
        <ReceivingWarehouseCell transfer={transfer} />
        {/* Lines up with a sales order's PTF badge in a mixed table. */}
        <td />
        <td
          className="whitespace-nowrap py-0.5 pr-2 text-muted-foreground"
          title={gt('Ordered', {
            context: 'line supply tab: tooltip of an open transfer order date',
          })}
        >
          {transfer.orderedDate ?? NO_FACT}
        </td>
        <td className="py-0.5 text-right tabular-nums">
          {formatQuantity(transfer.quantity)}
        </td>
      </tr>
      {open && tree !== undefined && transfer.receivingWarehouseId !== null && (
        <tr>
          <td colSpan={5} className="pb-1 pl-4">
            <ReceivingOpenOrders
              tree={tree}
              warehouseId={transfer.receivingWarehouseId}
              label={receivingLabel(transfer)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

const sum = (rows: readonly { quantity: number }[]) =>
  rows.reduce((total, row) => total + row.quantity, 0);

/**
 * The order lines behind one SKU's position at one location: the open POs and
 * inbound transfers that make On order, the open sales orders and staged
 * transfers that Net takes off, and the open transfers out with who receives
 * them (GEN-3284). Shared by the buyer board's Line footer (GEN-3285) and
 * Replenishment detail (GEN-3241).
 *
 * With `onlyReceipts`, only On order is shown, limited to those receipts.
 * With `tree`, open transfers out open the receiving warehouse's open orders.
 */
export function SupplyDemandLines({
  lines,
  onlyReceipts,
  now = new Date(),
  className,
  tree,
}: {
  lines: SupplyDemandLinesData;
  onlyReceipts?: LineReceipt[];
  now?: Date;
  className?: string;
  tree?: OpenOrdersTree;
}) {
  const gt = useGT();
  const receipts = onlyReceipts ?? lines.receipts;
  const today = formatDateOnlyLocal(now);
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-x-6 gap-y-3 p-3 text-xs lg:grid-cols-2 xl:grid-cols-4',
        className,
      )}
    >
      <Section
        title={
          <T context="line supply tab section: open POs and inbound transfers">
            On order
          </T>
        }
        total={sum(receipts)}
        empty={receipts.length === 0}
      >
        {receipts.map((receipt) => {
          const late = isPastDue(receipt.expectedDate, today);
          return (
            <tr key={receipt.lineId}>
              <td className="py-0.5 pr-2">
                <ReceiptReference receipt={receipt} />
              </td>
              <td
                className={
                  late
                    ? 'py-0.5 pr-2 font-medium text-amber-700 dark:text-amber-400'
                    : 'py-0.5 pr-2 text-muted-foreground'
                }
              >
                {receipt.expectedDate}
                {late &&
                  ` ${gt('past due', {
                    context: 'line supply tab: receipt expected before today',
                  })}`}
              </td>
              <td className="py-0.5 text-right tabular-nums">
                {formatQuantity(receipt.quantity)}
              </td>
            </tr>
          );
        })}
      </Section>
      {onlyReceipts ? null : (
        <>
          <Section
            title={
              <T context="line supply tab section: open sales orders; net takes off the larger of these and unshipped">
                Open sales orders
              </T>
            }
            total={sum(lines.salesOrders)}
            empty={lines.salesOrders.length === 0}
          >
            {lines.salesOrders.map((order) => (
              <SalesOrderRow key={order.lineId} order={order} />
            ))}
          </Section>
          <Section
            title={
              <T context="line supply tab section: staged transfers out that net available subtracts">
                Staged transfers out (in net)
              </T>
            }
            total={sum(lines.stagedTransfers)}
            empty={lines.stagedTransfers.length === 0}
          >
            {lines.stagedTransfers.map((transfer) => (
              <tr key={transfer.lineId}>
                <td className="py-0.5 pr-2 font-mono">
                  {transfer.trackingNo ?? NO_FACT}
                </td>
                <td className="py-0.5 pr-2 text-muted-foreground">
                  {transfer.toWarehouseName === null
                    ? NO_FACT
                    : gt('to {warehouse}', {
                        context:
                          'line supply tab: where a staged transfer goes',
                        warehouse: transfer.toWarehouseName,
                      })}
                </td>
                <td className="py-0.5 text-right tabular-nums">
                  {formatQuantity(transfer.quantity)}
                </td>
              </tr>
            ))}
          </Section>
          <Section
            title={
              <T context="line supply tab section: open AS400 transfers shipping out, counted in the unshipped quantity, not in net">
                Open transfers out (in unshipped)
              </T>
            }
            total={sum(lines.transfersOut)}
            empty={lines.transfersOut.length === 0}
          >
            {lines.transfersOut.map((transfer) => (
              <TransferOutRow
                key={transfer.lineId}
                transfer={transfer}
                tree={tree && { sku: tree.sku, path: [tree.warehouseId] }}
              />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}
